import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, Steps, Spin, Alert } from 'antd';
import {
  SearchOutlined,
  FileTextOutlined,
  FormOutlined,
  EyeOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { Provider } from 'react-redux';
import { store } from '../store';
import { ONBOARDING_PAGE_BACKGROUND_NEUTRAL } from '../styles/onboardingSurface';
import {
  useGetRestaurantInfoQuery,
  useLazyGetOrderSummaryQuery,
  useGenerarAutofacturaMutation,
  useGetRegimenesFiscalesQuery,
  useGetUsosCFDIQuery,
} from '../services/autofacturaApi';
import type { AutofacturaOrderSummary, AutofacturaGenerarRequest, AutofacturaResult } from '../types/autofactura';
import AutofacturaHeader from '../components/autofactura/AutofacturaHeader';
import AutofacturaLanding from '../components/autofactura/AutofacturaLanding';
import OrderLookupStep from '../components/autofactura/OrderLookupStep';
import OrderSummaryStep from '../components/autofactura/OrderSummaryStep';
import FiscalDataStep from '../components/autofactura/FiscalDataStep';
import ConfirmationStep from '../components/autofactura/ConfirmationStep';
import ResultStep from '../components/autofactura/ResultStep';

const { Step } = Steps;

const AutofacturaPortalInner: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const orderFromUrl = searchParams.get('order');
  const totalFromUrl = searchParams.get('total');
  // Cuenta dividida: el QR del ticket de cada cuenta trae su número para
  // facturar solo esa cuenta (un CFDI por cuenta)
  const cuentaFromUrl = searchParams.get('cuenta');

  // If no code, show landing page
  if (!code) {
    return <AutofacturaLanding />;
  }

  return (
    <AutofacturaWizard
      code={code}
      initialOrderNumber={orderFromUrl || undefined}
      initialTotal={totalFromUrl || undefined}
      initialCuenta={cuentaFromUrl || undefined}
    />
  );
};

interface AutofacturaWizardProps {
  code: string;
  initialOrderNumber?: string;
  initialTotal?: string;
  initialCuenta?: string;
}

const AutofacturaWizard: React.FC<AutofacturaWizardProps> = ({ code, initialOrderNumber, initialTotal, initialCuenta }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [order, setOrder] = useState<AutofacturaOrderSummary | null>(null);
  const [verifiedTotal, setVerifiedTotal] = useState<number>(0);
  const [fiscalData, setFiscalData] = useState<AutofacturaGenerarRequest | null>(null);
  const [result, setResult] = useState<AutofacturaResult | null>(null);
  const [orderError, setOrderError] = useState<string | undefined>();

  // API hooks
  const { data: restaurantInfo, isLoading: loadingInfo, error: infoError } = useGetRestaurantInfoQuery(code);
  const [getOrderSummary, { isFetching: loadingOrder }] = useLazyGetOrderSummaryQuery();
  const [generarAutofactura, { isLoading: generando }] = useGenerarAutofacturaMutation();
  const { data: regimenesFiscales, isLoading: loadingRegimenes } = useGetRegimenesFiscalesQuery(code);
  const { data: usosCFDI, isLoading: loadingUsos } = useGetUsosCFDIQuery(code);

  // Auto-search if order number and total come from URL
  useEffect(() => {
    if (initialOrderNumber && initialTotal && restaurantInfo?.autofacturaEnabled) {
      const num = parseInt(initialOrderNumber, 10);
      const tot = parseFloat(initialTotal);
      const cta = initialCuenta ? parseInt(initialCuenta, 10) : undefined;
      if (num > 0 && tot > 0) {
        handleSearchOrder(num, tot, cta && cta > 0 ? cta : undefined);
      }
    }
  }, [initialOrderNumber, initialTotal, restaurantInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchOrder = async (orderNumber: number, total: number, cuenta?: number) => {
    setOrderError(undefined);
    try {
      const summary = await getOrderSummary({ code, orderNumber, total, cuenta }).unwrap();
      setOrder(summary);
      setVerifiedTotal(total);
      setCurrentStep(1); // Go to order summary
    } catch (err: any) {
      setOrderError(err?.data?.error || 'Orden no encontrada');
    }
  };

  const handleFiscalDataReady = (data: AutofacturaGenerarRequest) => {
    setFiscalData(data);
    setCurrentStep(3); // Go to confirmation
  };

  const handleConfirmGenerar = async () => {
    if (!fiscalData) return;
    try {
      // cuentaNumber viene del summary: es la cuenta que el backend resolvió
      // (del QR o por monto) y a la que se registrará el CFDI
      const res = await generarAutofactura({
        code,
        data: { ...fiscalData, cuenta: order?.cuentaNumber },
      }).unwrap();
      setResult(res);
      setCurrentStep(4); // Go to result
    } catch (err: any) {
      setResult({
        success: false,
        error: err?.data?.error || 'No se pudo generar la factura. Por favor contacta al restaurante para asistencia.',
      });
      setCurrentStep(4);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setOrder(null);
    setVerifiedTotal(0);
    setFiscalData(null);
    setResult(null);
    setOrderError(undefined);
  };

  const handleRetryWithCorrection = () => {
    // Go back to fiscal data step (step 2), preserving order data
    setFiscalData(null);
    setResult(null);
    setCurrentStep(2);
  };

  // Loading state
  if (loadingInfo) {
    return (
      <PageWrapper>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      </PageWrapper>
    );
  }

  // Restaurant not found or error
  if (infoError || !restaurantInfo) {
    return (
      <PageWrapper>
        <Alert
          type="error"
          message="Restaurante no encontrado"
          description="Verifica el código del restaurante e intenta de nuevo."
          showIcon
        />
      </PageWrapper>
    );
  }

  // Autofactura not enabled
  if (!restaurantInfo.autofacturaEnabled) {
    return (
      <PageWrapper>
        <AutofacturaHeader restaurant={restaurantInfo} />
        <Alert
          type="info"
          message="Autofacturación no disponible"
          description="Este restaurante no tiene habilitada la autofacturación en este momento."
          showIcon
        />
      </PageWrapper>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <OrderLookupStep
            initialOrderNumber={initialOrderNumber}
            initialTotal={initialTotal}
            onSearch={handleSearchOrder}
            loading={loadingOrder}
            error={orderError}
          />
        );
      case 1:
        return order ? (
          <OrderSummaryStep
            order={order}
            onContinue={() => setCurrentStep(2)}
            onCancel={handleReset}
          />
        ) : null;
      case 2:
        return order ? (
          <FiscalDataStep
            code={code}
            orderNumber={order.orderNumber}
            total={verifiedTotal}
            regimenesFiscales={regimenesFiscales}
            usosCFDI={usosCFDI}
            catalogsLoading={loadingRegimenes || loadingUsos}
            onSubmit={handleFiscalDataReady}
            submitting={false}
          />
        ) : null;
      case 3:
        return order && fiscalData ? (
          <ConfirmationStep
            order={order}
            fiscalData={fiscalData}
            regimenesFiscales={regimenesFiscales}
            usosCFDI={usosCFDI}
            onConfirm={handleConfirmGenerar}
            onBack={() => setCurrentStep(2)}
            submitting={generando}
          />
        ) : null;
      case 4:
        return result ? (
          <ResultStep result={result} code={code} onReset={handleReset} onRetryCorrection={handleRetryWithCorrection} />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <PageWrapper>
      <AutofacturaHeader restaurant={restaurantInfo} />

      <Steps
        current={currentStep}
        size="small"
        style={{ marginBottom: 24 }}
        responsive={false}
      >
        <Step title="Buscar" icon={<SearchOutlined />} />
        <Step title="Orden" icon={<FileTextOutlined />} />
        <Step title="Datos" icon={<FormOutlined />} />
        <Step title="Resumen" icon={<EyeOutlined />} />
        <Step title="Resultado" icon={<CheckCircleOutlined />} />
      </Steps>

      {renderStepContent()}
    </PageWrapper>
  );
};

const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      background: ONBOARDING_PAGE_BACKGROUND_NEUTRAL,
      padding: '24px 16px',
    }}
  >
    <Card
      style={{
        maxWidth: 520,
        width: '100%',
        borderRadius: 12,
        boxShadow: '0 4px 24px var(--shadow-color)',
        marginTop: 24,
      }}
    >
      {children}
    </Card>
    <div style={{ textAlign: 'center', marginTop: 24, marginBottom: 16 }}>
      <a
        href="https://www.massresto.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <img
          src="/massresto-logo.png"
          alt="Massrestaurant"
          style={{ height: 28, objectFit: 'contain', opacity: 0.5, marginBottom: 4 }}
        />
        <div style={{ fontSize: 11, color: 'var(--color-neutral-text)', lineHeight: 1.3 }}>
          Powered by <strong style={{ color: 'var(--text-secondary)' }}>Massrestaurant</strong>
        </div>
        <div style={{ fontSize: 10, color: 'var(--color-neutral-text)' }}>
          www.massresto.com
        </div>
      </a>
    </div>
  </div>
);

// Wrap with Redux Provider since this is rendered outside the main app's Provider
const AutofacturaPortal: React.FC = () => (
  <Provider store={store}>
    <AutofacturaPortalInner />
  </Provider>
);

export default AutofacturaPortal;
